// LocalSession (§23.7): an offline World + BotManager + local input stepping at 60 Hz, producing a
// FrameView with source 'local'. Drives the menu diorama (Lobby, your bean, 4 Chill bots, dummies,
// respawn on fall) and Practice (chosen map and mode, 0–7 bots, practice dummies, the item hotkey
// menu on keys 1–6 or the touch item wheel, restart). Events go to the bus exactly as online:
// local predicted types about the local bean on 'sim:local', everything else on 'sim'.
//
// API (WP9b): start(cfg) · restart() · stop() · giveItem(kind) · frame(now) · config · active ·
// freePlay · localSlot · tick · currentWorld. Your bean follows the live profile (name, cosmetics
// and identity preference update in place, so the Locker shows on the diorama bean at once).
import { MAX_BEANS, NO_SLOT, TICK_HZ } from '@shared/constants';
import { BOT_NAMES } from '@shared/names';
import { BotManager } from '@shared/bots/manager';
import type { BotContext } from '@shared/bots/botTypes';
import { NO_IDENTITY_PREF, randomCosmetics } from '@shared/cosmetics';
import { Difficulty, EK, ItemKind, MapId, ModeId, MutatorId, Personality, RoomPhase } from '@shared/ids';
import { MAPS } from '@shared/maps/index';
import { MODES } from '@shared/modes/index';
import { giveItem, spawnEntity } from '@shared/sim/items';
import { randInt, randRange, seedRng, shuffle, type RngHolder } from '@shared/rng';
import { stepWorld } from '@shared/sim/step';
import { createWorld, pickSpawn } from '@shared/sim/world';
import type { InputFrame, RoundConfig, StepContext, World } from '@shared/types';
import type { Bus } from '../core/bus';
import { TICK_MS } from '../core/time';
import type { InputManager, AimContext, InputManagerFull } from '../input/InputManager';
import type { Profile } from '../profile/Profile';
import { LOCAL_PREDICTED_EVENTS } from './EventScheduler';
import { createFrameView, fillFrameFromWorld, type BeanIdentity, type FrameView } from './FrameView';

export interface LocalSessionConfig {
  mapId: MapId;
  modeId: ModeId;
  /** Bot count, 0–7. */
  bots: number;
  difficulty: Difficulty;
  /** Practice restarts the round after it ends; the menu diorama never ends. */
  practice: boolean;
  /** Practice dummies spawned on arenas (true = 2). The Lobby map brings its own. */
  dummies?: boolean | number;
  /** Crates and items on (default true). */
  items?: boolean;
}

/** §9.1: the menu diorama is the Lobby with your bean and 4 Chill bots. */
export const MENU_SESSION: Readonly<LocalSessionConfig> = { mapId: MapId.LOBBY, modeId: ModeId.LOBBY, bots: 4, difficulty: Difficulty.CHILL, practice: false };
/** The default Practice setup until the Practice screen (WP9) lets the player choose. */
export const DEFAULT_PRACTICE: Readonly<LocalSessionConfig> = { mapId: MapId.TEACUP, modeId: ModeId.ROYALE, bots: 3, difficulty: Difficulty.NORMAL, practice: true, dummies: true };
/** Practice dummies when `dummies: true`. */
export const PRACTICE_DUMMIES = 2;
const MAX_PRACTICE_DUMMIES = 4;
/** `Entity.data` of a practice dummy: no home in the map's prop list, so it respawns at the center. */
const PRACTICE_DUMMY_HOME = 0xff;

export interface LocalRoundInfo { mapId: MapId; modeId: ModeId; seed: number; golden: boolean }

export interface LocalSessionDeps {
  bus: Bus;
  input: InputManager;
  aim: AimContext;
  profile(): Profile;
  /** `?autoplay=1`: the bot brain drives the local bean too (§22). */
  autoplay: boolean;
  /** Every new world (start and Practice restarts): the renderer and audio rebuild for it. */
  onRound(info: LocalRoundInfo): void;
}

const LOCAL_SLOT = 0;
const MAX_STEPS_PER_FRAME = 8;
const MAX_FRAME_MS = 250;
const RESTART_DELAY_TICKS = 3 * TICK_HZ;
const SNAP_DISTANCE = 3;
const BOT_PITCH = { min: 0.9, max: 1.15 } as const;
const PERSONALITIES: readonly Personality[] = Object.values(Personality);

function newSeed(): number {
  return crypto.getRandomValues(new Uint32Array(1))[0];
}

export class LocalSession {
  private world: World | null = null;
  private cfg: LocalSessionConfig = MENU_SESSION;
  private readonly bots = new BotManager();
  private readonly view = createFrameView();
  private readonly players: (BeanIdentity | null)[] = new Array<BeanIdentity | null>(MAX_BEANS).fill(null);
  private readonly inputs: (InputFrame | null)[] = new Array<InputFrame | null>(MAX_BEANS).fill(null);
  private readonly stepCtx: StepContext = { predictSlot: NO_SLOT, rewindTicks: new Array<number>(MAX_BEANS).fill(0) };
  private readonly botCtx: BotContext = {
    matchScores: new Array<number>(MAX_BEANS).fill(0), crowned: NO_SLOT, humanSlots: [LOCAL_SLOT], reactionMult: 1, teamsOn: false,
  };
  private readonly prevX = new Float64Array(MAX_BEANS);
  private readonly prevY = new Float64Array(MAX_BEANS);
  private readonly prevZ = new Float64Array(MAX_BEANS);
  private readonly prevEnts: { id: number; x: number; y: number; z: number }[] = [];
  private prevEntCount = 0;
  private acc = 0;
  private lastNow = -1;
  private sessionTick = 0;
  private overTicks = 0;
  private roundId = 0;
  private freePlayValue = false;
  private lastProfile: Profile | null = null;

  constructor(private readonly deps: LocalSessionDeps) {}

  get active(): boolean {
    return this.world !== null;
  }

  get localSlot(): number {
    return LOCAL_SLOT;
  }

  get config(): Readonly<LocalSessionConfig> {
    return this.cfg;
  }

  /**
   * Practice with too few beans for the chosen mode (e.g. Royale with 0 bots) runs as free play:
   * the Lobby rules (respawn on fall, never ends) on the chosen map. The HUD can label it.
   */
  get freePlay(): boolean {
    return this.freePlayValue;
  }

  /** The authoritative offline world (debug hash). */
  get currentWorld(): World | null {
    return this.world;
  }

  /** Steps taken since `start` (the offline stand-in for the room tick). */
  get tick(): number {
    return this.sessionTick;
  }

  /** Starts (or restarts) a session on a fresh world. */
  start(cfg: LocalSessionConfig): void {
    this.cfg = cfg;
    const seed = newSeed();
    const rng: RngHolder = { rng: seedRng(seed) };
    const profile = this.deps.profile();
    const botCount = Math.max(0, Math.min(MAX_BEANS - 1, cfg.bots));

    const pref = profile.cosmetics.identityPref;
    const localIdentity = pref !== NO_IDENTITY_PREF ? pref : 0;
    const freeIdentities = Array.from({ length: MAX_BEANS }, (_, i) => i).filter((i) => i !== localIdentity);
    const names = shuffle(rng, [...BOT_NAMES]);

    this.players.fill(null);
    this.players[LOCAL_SLOT] = {
      name: profile.name, isBot: false, connected: true, identity: localIdentity, team: 0,
      cosmetics: profile.cosmetics, voicePitch: 1, score: 0,
    };
    for (let slot = 0; slot < MAX_BEANS; slot++) this.bots.setBot(slot, null);
    if (this.deps.autoplay) {
      this.bots.setBot(LOCAL_SLOT, { slot: LOCAL_SLOT, personality: Personality.TACTICIAN, difficulty: Difficulty.NORMAL, seed, name: profile.name });
    }
    for (let i = 0; i < botCount; i++) {
      const slot = LOCAL_SLOT + 1 + i;
      const name = names[i % names.length];
      this.players[slot] = {
        name, isBot: true, connected: true, identity: freeIdentities[i], team: 0,
        cosmetics: randomCosmetics(rng), voicePitch: randRange(rng, BOT_PITCH.min, BOT_PITCH.max), score: 0,
      };
      this.bots.setBot(slot, {
        slot, personality: PERSONALITIES[randInt(rng, PERSONALITIES.length)], difficulty: cfg.difficulty,
        seed: (seed + slot * 0x9e3779b9) >>> 0, name,
      });
    }

    // Too few beans for the mode's rules: free play on the chosen map (see `freePlay`).
    const beans = 1 + botCount;
    this.freePlayValue = cfg.practice && cfg.modeId !== ModeId.LOBBY && beans < MODES[cfg.modeId].minBeans;
    const modeId = this.freePlayValue ? ModeId.LOBBY : cfg.modeId;
    const roundCfg: RoundConfig = {
      mapId: cfg.mapId, modeId, mutator: MutatorId.NONE, seed, golden: false, itemsOn: cfg.items !== false, teamsOn: false,
      activeSlots: this.players.map((p) => p !== null),
      teams: new Array<number>(MAX_BEANS).fill(0),
      matchScores: new Array<number>(MAX_BEANS).fill(0),
      crownedSlot: NO_SLOT,
      startItems: new Array<ItemKind>(MAX_BEANS).fill(ItemKind.NONE),
    };
    this.roundId = (this.roundId + 1) & 255;
    this.world = createWorld(roundCfg, this.roundId, false);
    if (cfg.practice) this.spawnDummies(this.world, cfg);
    this.bots.reset(this.world);
    this.lastProfile = profile;
    this.bindHotkeys(cfg.practice);
    this.acc = 0;
    this.lastNow = -1;
    this.overTicks = 0;
    this.capturePrev(this.world);
    this.deps.onRound({ mapId: cfg.mapId, modeId, seed, golden: false });
  }

  /** Practice "restart": a fresh round with the same setup. */
  restart(): void {
    this.start(this.cfg);
  }

  stop(): void {
    this.world = null;
    this.bindHotkeys(false);
  }

  /**
   * Practice item hotkeys (§23.7): gives the local bean an item now. Ignored outside Practice and
   * while the local bean cannot hold one (out, ghost).
   */
  giveItem(kind: ItemKind): boolean {
    const w = this.world;
    if (!w || !this.cfg.practice || kind === ItemKind.NONE) return false;
    const b = w.beans[LOCAL_SLOT];
    if (!b.active || b.out) return false;
    giveItem(w, LOCAL_SLOT, kind);
    this.dispatch(w);
    return true;
  }

  private bindHotkeys(on: boolean): void {
    const input = this.deps.input as Partial<InputManagerFull>;
    if (typeof input.setItemHotkeys !== 'function') return;
    input.setItemHotkeys(on ? (kind) => {
      this.giveItem(kind);
    } : null);
  }

  /** Practice dummies on maps without their own (the Lobby has dummy posts). */
  private spawnDummies(w: World, cfg: LocalSessionConfig): void {
    if (cfg.mapId === MapId.LOBBY || cfg.dummies === false || cfg.dummies === undefined) return;
    const n = cfg.dummies === true ? PRACTICE_DUMMIES : Math.max(0, Math.min(MAX_PRACTICE_DUMMIES, Math.floor(cfg.dummies)));
    for (let i = 0; i < n; i++) {
      const at = pickSpawn(w, true);
      spawnEntity(w, EK.DUMMY, NO_SLOT, at.x, at.y, 0).data = PRACTICE_DUMMY_HOME;
    }
  }

  /** Your bean follows the live profile: name, cosmetics and identity preference (Locker edits). */
  private syncProfile(): void {
    const profile = this.deps.profile();
    if (profile === this.lastProfile) return;
    this.lastProfile = profile;
    const me = this.players[LOCAL_SLOT];
    if (!me) return;
    me.name = profile.name;
    me.cosmetics = profile.cosmetics;
    const pref = profile.cosmetics.identityPref;
    if (pref !== NO_IDENTITY_PREF && pref !== me.identity) {
      // Swap with whoever wears that identity so all 8 stay unique.
      const other = this.players.find((p, i) => i !== LOCAL_SLOT && p !== null && p.identity === pref);
      if (other) other.identity = me.identity;
      me.identity = pref;
    }
  }

  /** Advances the sim to `now` (ms) and returns the interpolated frame. */
  frame(now: number): FrameView {
    const dtMs = this.lastNow < 0 ? 0 : Math.min(MAX_FRAME_MS, now - this.lastNow);
    this.lastNow = now;
    if (!this.world) return this.view;
    this.syncProfile();

    this.acc += dtMs;
    let steps = 0;
    // A Practice restart inside step() swaps the world and zeroes the accumulator.
    while (this.world && this.acc >= TICK_MS && steps < MAX_STEPS_PER_FRAME) {
      this.step(this.world);
      this.acc -= TICK_MS;
      steps++;
    }
    if (steps === MAX_STEPS_PER_FRAME) this.acc = Math.min(this.acc, TICK_MS);
    const w = this.world;

    const alpha = this.acc / TICK_MS;
    const view = fillFrameFromWorld(this.view, w, {
      now, dt: dtMs / 1000, source: 'local',
      roomPhase: this.roomPhase(w), roundIndex: 0, totalRounds: this.cfg.practice ? 1 : 0,
      localSlot: LOCAL_SLOT, focusSlot: LOCAL_SLOT,
      tick: w.tick - 1 + alpha,
      players: this.players, map: MAPS[w.cfg.mapId],
    });
    this.interpolate(view, alpha);
    return view;
  }

  private roomPhase(w: World): RoomPhase {
    if (!this.cfg.practice) return RoomPhase.LOBBY;
    if (w.over) return RoomPhase.ROUND_END;
    return w.tick < 0 ? RoomPhase.INTRO : RoomPhase.PLAY;
  }

  private step(w: World): void {
    this.capturePrev(w);
    this.sessionTick++;
    this.inputs.fill(null);
    if (!this.deps.autoplay) this.inputs[LOCAL_SLOT] = this.deps.input.sample(this.sessionTick, this.deps.aim);
    this.bots.inputsFor(w, this.botCtx, this.inputs);
    stepWorld(w, this.inputs, this.stepCtx);
    this.bots.observe(w);
    this.dispatch(w);
    if (this.cfg.practice && w.over && ++this.overTicks >= RESTART_DELAY_TICKS) this.start(this.cfg);
  }

  private dispatch(w: World): void {
    const bus = this.deps.bus;
    for (const ev of w.events) {
      if (ev.a === LOCAL_SLOT && LOCAL_PREDICTED_EVENTS.includes(ev.type)) bus.emit('sim:local', { ev, local: true });
      else bus.emit('sim', { ev, local: false });
    }
    w.events.length = 0;
  }

  private capturePrev(w: World): void {
    for (let i = 0; i < MAX_BEANS; i++) {
      const b = w.beans[i];
      this.prevX[i] = b.pos.x;
      this.prevY[i] = b.pos.y;
      this.prevZ[i] = b.z;
    }
    for (let i = 0; i < w.ents.length; i++) {
      const e = w.ents[i];
      const p = this.prevEnts[i] ?? (this.prevEnts[i] = { id: 0, x: 0, y: 0, z: 0 });
      p.id = e.id;
      p.x = e.pos.x;
      p.y = e.pos.y;
      p.z = e.z;
    }
    this.prevEntCount = w.ents.length;
  }

  /** Renders between the previous and current step so motion is smooth at any refresh rate. */
  private interpolate(view: FrameView, alpha: number): void {
    for (let i = 0; i < MAX_BEANS; i++) {
      const b = view.beans[i];
      if (!b.active) continue;
      const px = this.prevX[i];
      const py = this.prevY[i];
      if (Math.hypot(b.x - px, b.y - py) > SNAP_DISTANCE) continue;
      b.x = px + (b.x - px) * alpha;
      b.y = py + (b.y - py) * alpha;
      b.z = this.prevZ[i] + (b.z - this.prevZ[i]) * alpha;
    }
    for (const e of view.ents) {
      for (let i = 0; i < this.prevEntCount; i++) {
        const p = this.prevEnts[i];
        if (p.id !== e.id) continue;
        if (Math.hypot(e.x - p.x, e.y - p.y) <= SNAP_DISTANCE) {
          e.x = p.x + (e.x - p.x) * alpha;
          e.y = p.y + (e.y - p.y) * alpha;
          e.z = p.z + (e.z - p.z) * alpha;
        }
        break;
      }
    }
  }
}
